import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { flowRight as compose, isEmpty, sortBy } from 'lodash';
import { DateTime } from 'luxon';
import { Typography } from '@material-ui/core';
import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles';
import { MockApolloClient } from 'mock-apollo-client';
import { NormalizedCacheObject } from 'apollo-cache-inmemory';
import ApolloClient from 'apollo-client';
import { useFlags } from 'launchdarkly-react-client-sdk';

import MHButton from '../../MHButton';
import {
  SessionsQueryQuery as SessionsQuery,
  useSessionsQueryQuery as useSessionsQuery,
} from '../documents/SessionsQuery.generated';
import MHSkeleton from '../../MHSkeleton';
import { PaginationButtons } from '../../PaginationButtons';
import { getCurrentUser } from '../../../selectors/userSelectors';
import ScheduleSessionModal from '../../Sessions/SessionScheduling/ScheduleSessionModal/ScheduleSessionModal';
import { FormattedCoachingVisit } from '../../../models/Sessions';
import { Member } from '../../../models/Common';
import { LastInLineScrollLoader } from '../LastInLineLoader';
import SessionsByDay from './SessionsByDay/SessionsByDay';
import MemberProfileDrawer from '../../MemberProfileDrawer/MemberProfileDrawer';
import { bareSessionObj, getEndOfMonth, mergeSessions, prepareUpcomingSessions } from './helpers';
import {
  initialPaginationInfo,
  SessionsListTestID,
  sessionViews,
  NUM_SESSIONS_TO_LOAD,
} from './constants';
import { colors } from '../../../themes/colors';
import SessionSchedulerModal from '../SessionScheduling/SessionScheduler/SessionSchedulerModal';

type Props = WithStyles<typeof styles> & {
  loading?: boolean;
  memberClient: ApolloClient<NormalizedCacheObject> | MockApolloClient;
} & RouteComponentProps;

const SessionsList: React.FC<Props> = ({ classes, history, memberClient, loading = false }) => {
  const { localTimezone, isPsychiatrist } = useSelector(getCurrentUser);
  const { hideProviderCalendaring } = useFlags();
  const currentDateTime = useRef(DateTime.local().setZone(localTimezone));
  const initialDateTime = currentDateTime.current;

  const [currentView, setCurrentView] = useState(sessionViews.Upcoming);
  const [scheduledTimeAfter, setScheduledTimeAfter] = useState(initialDateTime.minus({ hours: 1 }));
  const [scheduledTimeBefore, setScheduledTimeBefore] = useState(getEndOfMonth(initialDateTime));
  const [isSchedulingSession, setIsSchedulingSession] = useState(false);
  const [sessionsInView, setSessionsInView] = useState<Record<number, FormattedCoachingVisit[]>>(
    bareSessionObj(),
  );
  const [paginationInfo, setPaginationInfo] = useState(initialPaginationInfo);
  const [visibleMemberProfileDrawer, setVisibleMemberProfileDrawer] = useState<Member | null>(null);

  const handleSessionFetch = (sessionsData: SessionsQuery | undefined, replace = false) => {
    setSessionsInView((currentSessions) =>
      mergeSessions(replace ? bareSessionObj() : currentSessions, sessionsData),
    );

    setPaginationInfo((previousInfo) => {
      if (previousInfo && data?.providerCoachingVisits) {
        return {
          hasNextPage: data.providerCoachingVisits.pageInfo.hasNextPage ?? false,
          loadedCount: previousInfo.loadedCount + data.providerCoachingVisits.edges.length,
          pageCursor: data.providerCoachingVisits.pageInfo.endCursor ?? '',
          totalCount: data.providerCoachingVisits.totalCount ?? 0,
        };
      } else {
        return initialPaginationInfo;
      }
    });
  };

  const {
    data,
    fetchMore,
    loading: queryLoading,
  } = useSessionsQuery({
    client: memberClient,
    onCompleted: handleSessionFetch,
    variables: {
      first: NUM_SESSIONS_TO_LOAD,
      orderBy: 'scheduled_time',
      scheduledTimeAfter,
      scheduledTimeBefore,
    },
  });

  const showSkeleton = loading || queryLoading;

  const displayThisMonthUpcomingSessions = () => {
    const rightNow = DateTime.local().setZone(localTimezone);

    currentDateTime.current = rightNow.minus({ hour: 1 });

    setPaginationInfo({
      hasNextPage: false,
      loadedCount: 0,
      pageCursor: '',
      totalCount: 0,
    });

    setSessionsInView(prepareUpcomingSessions(rightNow));

    setCurrentView(sessionViews.Upcoming);
    setScheduledTimeAfter(rightNow.minus({ hour: 1 }));
    setScheduledTimeBefore(getEndOfMonth(rightNow));
  };

  const changeMonth = (fwd: boolean) => {
    const rightNow = DateTime.local().setZone(localTimezone);
    const showUpcomingMonth =
      (fwd && rightNow.month === currentDateTime.current.month && scheduledTimeAfter.day === 1) ||
      (!fwd && currentDateTime.current.month === rightNow.month + 1);

    if (showUpcomingMonth) {
      return displayThisMonthUpcomingSessions();
    }

    if (fwd) {
      currentDateTime.current = currentDateTime.current.startOf('month').plus({ month: 1 });
    } else {
      currentDateTime.current = currentDateTime.current.startOf('month').minus({
        months: scheduledTimeAfter.day === 1 ? 1 : 0,
      });
    }
    setSessionsInView(bareSessionObj());
    setPaginationInfo({
      hasNextPage: false,
      loadedCount: 0,
      pageCursor: '',
      totalCount: 0,
    });

    const thisMonth = rightNow.startOf('month');
    const nextView =
      (thisMonth > currentDateTime.current && sessionViews.Previous) ||
      (thisMonth < currentDateTime.current && sessionViews.Upcoming) ||
      sessionViews.Current;

    setScheduledTimeAfter(currentDateTime.current);
    setScheduledTimeBefore(getEndOfMonth(currentDateTime.current));
    setCurrentView(nextView);
  };

  const previousMonth = () => changeMonth(false);
  const nextMonth = () => changeMonth(true);

  const hasSessions =
    paginationInfo.totalCount > 0 &&
    Object.values(sessionsInView).some((dateObj) => !isEmpty(dateObj));

  const loadMoreSessions = () =>
    new Promise<void>((resolve) => {
      if (fetchMore) {
        fetchMore({
          variables: {
            after: paginationInfo.pageCursor,
          },
          updateQuery: (previous, { fetchMoreResult }) => {
            const prevEdges = previous?.providerCoachingVisits?.edges ?? [];
            const nextEdges = fetchMoreResult?.providerCoachingVisits?.edges ?? [];
            const totalCount = fetchMoreResult?.providerCoachingVisits?.totalCount ?? 0;

            handleSessionFetch(fetchMoreResult);

            return {
              providerCoachingVisits: {
                edges: [...prevEdges, ...nextEdges],
                pageInfo: fetchMoreResult?.providerCoachingVisits?.pageInfo ?? {
                  hasNextPage: false,
                  loadedCount: prevEdges.length + nextEdges.length,
                  pageCursor: '',
                  totalCount,
                },
                totalCount,
              },
            };
          },
        }).finally(() => {
          resolve();
        });
      }
    });

  const refreshCurrentSessions = () => {
    if (fetchMore) {
      fetchMore({
        variables: {
          first: paginationInfo.loadedCount,
          scheduledTimeAfter,
          scheduledTimeBefore,
          orderBy: 'scheduled_time',
        },
        updateQuery: (previous, { fetchMoreResult }) => {
          if (!fetchMoreResult) return previous;

          handleSessionFetch(fetchMoreResult, true);
          return fetchMoreResult;
        },
      });
    }
  };

  const showScheduleSessionModal = !showSkeleton && !hideProviderCalendaring && isSchedulingSession;
  const showScheduleSessionButton = !hideProviderCalendaring && !showSkeleton;
  const showNoSessionSchdeduledMsg = !showSkeleton && !hasSessions;

  const renderScheduleFormHelper = () => {
    if (showScheduleSessionModal) {
      if (isPsychiatrist)
        return (
          <div data-testid={SessionsListTestID.ScheduleSessionModal}>
            <SessionSchedulerModal
              isVisible={showScheduleSessionModal}
              memberClient={memberClient}
              onClose={() => setIsSchedulingSession(false)}
            />
          </div>
        );
      else
        return (
          <div data-testid={SessionsListTestID.ScheduleSessionModal}>
            <ScheduleSessionModal
              memberClient={memberClient}
              onClose={() => setIsSchedulingSession(false)}
            />
          </div>
        );
    }
  };

  return (
    <div>
      <div className={classes.topBar}>
        <div className={classes.monthHeader}>
          {showSkeleton && <MHSkeleton width="48" />}

          {!showSkeleton && (
            <>
              <Typography className={classes.monthText}>{currentView} sessions for</Typography>

              <PaginationButtons
                canDecrement
                canIncrement
                date={currentDateTime.current.toFormat('MMMM y')}
                handleDecrement={previousMonth}
                handleIncrement={nextMonth}
              />
            </>
          )}
        </div>

        {showScheduleSessionButton && (
          <MHButton
            extraClasses={[classes.scheduleButton]}
            onClick={() => setIsSchedulingSession(true)}
          >
            Schedule a session
          </MHButton>
        )}
      </div>

      <div className={classes.dayList}>
        {showNoSessionSchdeduledMsg && <Typography>You have no sessions scheduled.</Typography>}

        {hasSessions && (
          <div>
            {sortBy(Object.keys(sessionsInView), 'asc').map((day) => {
              if (isEmpty(sessionsInView[day])) {
                return null;
              }

              return (
                <SessionsByDay
                  currentDateTime={currentDateTime.current}
                  day={day}
                  history={history}
                  isUpcomingView={currentView === sessionViews.Upcoming}
                  key={day}
                  localTimezone={localTimezone}
                  memberClient={memberClient}
                  onVisibleMemberProfileDrawer={setVisibleMemberProfileDrawer}
                  refreshCurrentSessions={refreshCurrentSessions}
                  sessionsByDay={sessionsInView[day]}
                />
              );
            })}

            <LastInLineScrollLoader
              hasMore={!queryLoading && paginationInfo.hasNextPage}
              loaderProps={{ size: 30 }}
              loadMore={loadMoreSessions}
            />

            {visibleMemberProfileDrawer && (
              <div className={classes.drawerBuffer}>
                <MemberProfileDrawer
                  memberUuid={visibleMemberProfileDrawer.uuid}
                  onClickExit={() => setVisibleMemberProfileDrawer(null)}
                />
              </div>
            )}
          </div>
        )}
      </div>
      {renderScheduleFormHelper()}
    </div>
  );
};

const styles = () =>
  createStyles({
    dayList: {
      borderBottom: `2px solid ${colors.gray12}`,
      display: 'flex',
      flexDirection: 'column',
      paddingLeft: 15,
    },
    drawerBuffer: {
      width: 0,
    },
    monthHeader: {
      alignItems: 'center',
      display: 'flex',
      flex: 1,
      flexDirection: 'row',
      padding: '0.5rem',
    },
    monthText: {
      color: colors.black3,
      flexShrink: 0,
      fontSize: 16,
      fontWeight: 900,
      width: 'auto',
    },
    scheduleButton: {
      backgroundColor: colors.blue6,
      '&:hover': {
        backgroundColor: colors.blue6,
        boxShadow: 'none',
      },
    },
    topBar: {
      alignItems: 'center',
      borderBottom: `2px solid ${colors.gray1}`,
      display: 'flex',
      flex: 0,
      flexDirection: 'row',
      justifyContent: 'space-between',
      padding: 5,
    },
  });

export default compose(withStyles(styles), withRouter)(SessionsList);
